import { useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LoginLogCard } from "@/components/LoginLogCard";
import {
  Archive, FileArrowDown, ShieldCheck, Calendar, Warning,
} from "@phosphor-icons/react";

const CSVS = [
  { kind: "buchungen",   label: "Buchungen",    hint: "Zeitraum, Zimmer, Preis, Provision, Netto" },
  { kind: "gaeste",      label: "Gäste",        hint: "Kontakt + Rechnungsadresse" },
  { kind: "rechnungen",  label: "Rechnungen",   hint: "Positionen, Fällig, Zahlung, Status" },
  { kind: "ausgaben",    label: "Ausgaben",     hint: "Kategorien, Beträge, Beleg vorhanden ja/nein" },
  { kind: "anfragen",    label: "Anfragen",     hint: "Kontaktformular-Einsendungen" },
  { kind: "nachrichten", label: "Nachrichten",  hint: "Gesendete E-Mails & WhatsApp-Log" },
];

// Direkt-Download via <a href> — nutzt Cookie-Auth
function download(href, filename) {
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function Backup() {
  const [busy, setBusy] = useState("");

  const downloadCsv = async (kind, label) => {
    setBusy(kind);
    try {
      const res = await api.get(`/backup/csv/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      download(url, `${kind}.csv`);
      URL.revokeObjectURL(url);
      toast.success(`${label} exportiert`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Export");
    } finally { setBusy(""); }
  };

  const downloadZip = async () => {
    setBusy("zip");
    try {
      const res = await api.get(`/backup/zip`, { responseType: "blob" });
      const stamp = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(res.data);
      download(url, `msb_backup_${stamp}.zip`);
      URL.revokeObjectURL(url);
      toast.success("Voll-Backup heruntergeladen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim ZIP-Export");
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="backup-page">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <ShieldCheck size={22} weight="duotone" className="text-primary"/>
        </div>
        <div className="flex-1">
          <h1 className="text-3xl font-bold" style={{ fontFamily: "'Work Sans',sans-serif" }}>Datensicherung</h1>
          <p className="text-muted-foreground mt-1">
            Lade deine kompletten Cockpit-Daten als CSV herunter — für Buchhaltung, Steuerberater oder als Offsite-Backup.
          </p>
        </div>
      </div>

      <LoginLogCard />

      {/* Voll-Backup */}
      <div className="msb-card bg-gradient-to-br from-primary/5 to-transparent border-primary/20">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-primary flex items-center justify-center flex-shrink-0">
            <Archive size={26} weight="duotone" className="text-white"/>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold mb-1" style={{ fontFamily: "'Work Sans',sans-serif" }}>Alles als ZIP</h2>
            <p className="text-sm text-muted-foreground mb-3">
              Enthält <strong>alle 6 CSV-Exporte</strong> + <strong>alle Rechnungs-PDFs</strong> + <strong>alle Ausgaben-Belege</strong> (Original-Dateien).
              Empfohlen für dein monatliches Offsite-Backup.
            </p>
            <Button
              onClick={downloadZip}
              disabled={busy === "zip"}
              className="rounded-xl bg-primary gap-2"
              data-testid="backup-zip-btn">
              <Archive size={18} weight="bold"/>
              {busy === "zip" ? "Erstelle ZIP…" : "Voll-Backup jetzt herunterladen"}
            </Button>
          </div>
        </div>
      </div>

      {/* Einzel-CSVs */}
      <div>
        <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: "'Work Sans',sans-serif" }}>
          Einzel-Exporte (CSV)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {CSVS.map(x => (
            <button
              key={x.kind}
              onClick={() => downloadCsv(x.kind, x.label)}
              disabled={busy === x.kind}
              className="msb-card text-left hover:border-primary hover:shadow-md transition-all flex items-center gap-3 p-4 disabled:opacity-60"
              data-testid={`backup-csv-${x.kind}`}>
              <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                <FileArrowDown size={20} weight="duotone" className="text-primary"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{x.label}</div>
                <div className="text-xs text-muted-foreground truncate">{x.hint}</div>
              </div>
              <span className="text-xs font-semibold text-primary whitespace-nowrap">
                {busy === x.kind ? "…" : "CSV ↓"}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Auto-Backup Info */}
      <div className="msb-card bg-emerald-50/50 border-emerald-200">
        <div className="flex items-start gap-3">
          <Calendar size={22} weight="duotone" className="text-emerald-700 mt-0.5"/>
          <div>
            <h3 className="font-semibold text-emerald-900">Automatische Monats-Sicherung</h3>
            <p className="text-sm text-emerald-800/90 mt-1">
              Am <strong>1. jedes Monats um 07:00 Uhr</strong> erzeugt der Server automatisch dieses ZIP-Paket
              und schickt es per E-Mail (von info@mainspessartblick.de) an <code className="bg-white/60 px-1.5 py-0.5 rounded text-xs">info@mainspessartblick.de</code>.
              Betreff: <em>„MSB Datensicherung — Monat Jahr"</em>. Archiviere diese Mails am besten in einem separaten Label/Ordner.
            </p>
          </div>
        </div>
      </div>

      {/* Sicherheits-Hinweise */}
      <div className="msb-card">
        <div className="flex items-start gap-3">
          <Warning size={22} weight="duotone" className="text-amber-600 mt-0.5"/>
          <div className="flex-1 text-sm">
            <h3 className="font-semibold mb-1">So schützt du dich zusätzlich</h3>
            <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
              <li>Lege die Monats-Mails in einem separaten Ordner ab (nicht löschen).</li>
              <li>Kopiere das ZIP zusätzlich in <strong>Google Drive</strong> oder <strong>OneDrive</strong> — dann hast du 2 Cloud-Kopien.</li>
              <li>1x pro Quartal: ZIP auf einen lokalen USB-Stick als „Kalte" Kopie.</li>
              <li>Die MongoDB selbst wird von Emergent zusätzlich mit täglichen Snapshots gesichert (Cloud-Managed).</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
